import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../../services/subscriptionApi';
import './Pricing.css';

const formatPrice = (cents) => {
  if (!cents || cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(0)}`;
};

export default function PricingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeMessage, setUpgradeMessage] = useState(null);

  useEffect(() => {
    const msg = sessionStorage.getItem('upgradeReason');
    if (msg) {
      setUpgradeMessage(msg);
      sessionStorage.removeItem('upgradeReason');
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [plansRes, currentRes] = await Promise.all([
          subscriptionApi.listPlans(),
          subscriptionApi.getCurrent().catch(() => null),
        ]);
        setPlans(plansRes?.plans || []);
        setCurrent(currentRes?.subscription || null);
      } catch (e) {
        console.error('Failed to load plans', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="pricing-page">
        <div className="pricing-loading">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      {upgradeMessage && (
        <div className="upgrade-alert">{upgradeMessage}</div>
      )}

      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Start with a free trial. Upgrade anytime as your needs grow.</p>
        {current?.status === 'trialing' && (
          <div className="trial-banner">
            You're on a free trial — {current.trialEndDate
              ? `expires ${new Date(current.trialEndDate).toLocaleDateString()}`
              : '5 free queries included'}
          </div>
        )}
      </div>

      <div className="pricing-grid">
        {plans.map((plan) => {
          const isCurrentPlan = current?.planId === plan.planId && current?.status === 'active';
          const features = plan.features || {};

          return (
            <div
              key={plan.planId}
              className={`pricing-card ${isCurrentPlan ? 'current' : ''} ${plan.displayOrder === 2 ? 'featured' : ''}`}
            >
              {plan.displayOrder === 2 && <div className="featured-badge">Most Popular</div>}
              {isCurrentPlan && <div className="current-badge">Current Plan</div>}

              <h2>{plan.planName}</h2>
              <p className="plan-description">{plan.description}</p>

              <div className="plan-price">
                <span className="price-amount">{formatPrice(plan.monthlyPriceCents)}</span>
                <span className="price-period">/month</span>
              </div>

              {plan.yearlyPriceCents && (
                <div className="yearly-price">
                  or {formatPrice(plan.yearlyPriceCents)}/year (save {Math.round((1 - plan.yearlyPriceCents / (plan.monthlyPriceCents * 12)) * 100)}%)
                </div>
              )}

              <ul className="plan-features">
                <li>{plan.includedQueries.toLocaleString()} queries/month</li>
                {features.maxGraphs && (
                  <li>{features.maxGraphs === -1 ? 'Unlimited' : features.maxGraphs} graphs</li>
                )}
                {features.maxDocuments && (
                  <li>{features.maxDocuments === -1 ? 'Unlimited' : features.maxDocuments} documents</li>
                )}
                {features.apiAccess && <li>API access</li>}
                {features.sso && <li>SSO / SAML</li>}
                {features.support && <li>{features.support} support</li>}
              </ul>

              <button
                className={`plan-cta ${isCurrentPlan ? 'disabled' : ''}`}
                disabled={isCurrentPlan}
                onClick={() => {
                  if (!isCurrentPlan) {
                    navigate(`/user-home/checkout?planId=${plan.planId}`);
                  }
                }}
              >
                {isCurrentPlan ? 'Current Plan' : 'Get Started'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
